import {ApplicationConfig, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection} from '@angular/core';
import {provideRouter} from '@angular/router';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {provideTranslateService} from '@ngx-translate/core';
import {provideTranslateHttpLoader} from '@ngx-translate/http-loader';
import {definePreset} from '@primeuix/themes';

import {routes} from './app.routes';
import {providePrimeNG} from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import {provideNgOpenapi} from '../api';
import {provideSignalFormsConfig} from '@angular/forms/signals';
import {NG_STATUS_CLASSES} from '@angular/forms/signals/compat';
import {provideTanStackQuery, QueryClient} from '@tanstack/angular-query-experimental';
import {AuthInterceptor} from './auth/auth.interceptor';
import {BlobSafeDateTransformInterceptor} from './interceptors/blob-safe-date-transform.interceptor';
import {DateOnlyParamInterceptor} from './interceptors/date-only-param.interceptor';
import {WallClockBodyInterceptor} from './interceptors/wall-clock-body.interceptor';
import {PatchBodyInterceptor} from './interceptors/patch-body.interceptor';
import {BusinessRuleInterceptor} from './interceptors/business-rule.interceptor';
import {ForbiddenInterceptor} from './interceptors/forbidden.interceptor';
import {GenericErrorInterceptor} from './interceptors/generic-error.interceptor';
import {MessageService} from 'primeng/api';
import {environment} from '../environments/environment.dev';


const MyPreset = definePreset(Aura, {
  primitive: {
    fontFamily: "'Gotham', sans-serif"
  },
  semantic: {
    fontFamily: "'Gotham', sans-serif",
    primary: {
      50: '#e6f0f7',
      100: '#b3d4e8',
      200: '#80b8d9',
      300: '#4d9cca',
      400: '#2680b4',
      500: '#064775',
      600: '#053d65',
      700: '#043254',
      800: '#032844',
      900: '#021d33',
      950: '#011322'
    },
    formField: {
      paddingX: '0.625rem',
      paddingY: '0.375rem'
    }
  }
});


export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    MessageService,
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    {provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: BusinessRuleInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: ForbiddenInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: GenericErrorInterceptor, multi: true},
    provideSignalFormsConfig({
      classes: NG_STATUS_CLASSES
    }),


    provideTanStackQuery(
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: true, // enabled by default
            staleTime: 30_000, // 30s before considered stale
          },
        },
      })
    ),


    provideNgOpenapi({
      basePath: environment.apiUrl,
      enableDateTransform: false,
      interceptors: [BlobSafeDateTransformInterceptor, DateOnlyParamInterceptor, WallClockBodyInterceptor, PatchBodyInterceptor]
    }),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: false,
        }

      }
    }),

    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/i18n/',
        suffix: '.json'
      }),
      fallbackLang: 'fr-BE',
      lang: localStorage.getItem('lang') ?? 'fr-BE'
    })
  ]
};


